import { useContext, useMemo, useState, useEffect } from 'react'
import {
  Box,
  Popover,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Checkbox,
  useTheme,
  Theme,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { SVGIcon } from 'src/images'
import { ArrowForwardIos as ArrowForwardIosIcon } from '@material-ui/icons'
import { InstallsContext } from 'src/context/gamePlayInsights/installs'

const getUseStyles = (theme: Theme) =>
  makeStyles(() => {
    return {
      popover: {
        '& .MuiPaper-root': {
          minWidth: '300px',
          width: '300px',
          maxHeight: '394px',
          marginTop: '4px',
          borderRadius: '6px',
          padding: '16px 12px',
          background: theme.palette.custom.dialogBg,
        },
      },
    }
  })

const SortButton = () => {
  const theme: Theme = useTheme()
  const useStyles = getUseStyles(theme)
  const classes = useStyles()
  const { state, dispatch } = useContext(InstallsContext)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [showListNumber, setShowListNumber] = useState<number>(8)
  const showList = useMemo(() => {
    const list = state.chartList
    if (state.view === 'country' && showListNumber < list.length) {
      const temp = list.slice(0, showListNumber)
      if (showListNumber > 8) {
        const newTemp = [
          ...state.checkedChartLine,
          ...list
            .slice(showListNumber - 8, showListNumber)
            .map((item: any) => item.label),
        ]
        dispatch({
          type: 'checkedChartLine',
          payload: newTemp,
        })
      }
      return temp
    }
    return list
    // eslint-disable-next-line
  }, [state.chartList, state.view, showListNumber])

  const installsHandleClick = (value: string) => {
    const selectedIndex = state.checkedChartLine.indexOf(value)
    let newSelected: string[] = []
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(state.checkedChartLine, value)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(state.checkedChartLine.slice(1))
    } else if (selectedIndex === state.checkedChartLine.length - 1) {
      newSelected = newSelected.concat(state.checkedChartLine.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        state.checkedChartLine.slice(0, selectedIndex),
        state.checkedChartLine.slice(selectedIndex + 1)
      )
    }
    dispatch({
      type: 'checkedChartLine',
      payload: newSelected,
    })
  }
  const checked = (value: string) => {
    return state.checkedChartLine.includes(value)
  }
  const handleOpen = (event: any) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  useEffect(() => {
    if (state.view === 'country') {
      setShowListNumber(8)
    }
  }, [state.view, state.chartList])
  useEffect(() => {
    if (state.view === 'country') {
      const checkedChartLine = state.chartList
        .slice(0, 8)
        .map((item: any) => item.label)
      dispatch({
        type: 'checkedChartLine',
        payload: checkedChartLine,
      })
    }
    // eslint-disable-next-line
  }, [state.chartList])
  return (
    <>
      <Box
        sx={{
          boxSizing: 'border-box',
          width: '24px',
          height: '24px',
          padding: '3px',
          cursor: 'pointer',
        }}
        aria-describedby={id}
        onClick={handleOpen}
      >
        <Tooltip title="Filter graph legends" arrow placement="top">
          <Box>
            <SVGIcon.SettingSVG
              color={theme.palette.text.primary}
              style={{ verticalAlign: 'text-top', lineHeight: '18px' }}
            />
          </Box>
        </Tooltip>
      </Box>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        className={classes.popover}
        sx={{ zIndex: 1999 }}
      >
        <TableContainer>
          <Table aria-label="simple table">
            <TableBody>
              {showList.map((row: any, index: number) => (
                <TableRow
                  key={index}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  onClick={() => {
                    installsHandleClick(row.value)
                  }}
                  role="checkbox"
                  aria-checked={checked(row.value)}
                  tabIndex={-1}
                  selected={checked(row.value)}
                  style={{
                    cursor: 'pointer',
                    background: theme.palette.custom.chartTableBg,
                  }}
                >
                  <TableCell
                    padding="checkbox"
                    style={{ padding: 0, width: 42 }}
                  >
                    <Checkbox
                      color="primary"
                      checked={checked(row.value)}
                      style={{ color: '#7487AA' }}
                    />
                  </TableCell>
                  <TableCell
                    component="th"
                    scope="row"
                    style={{ padding: 0, width: 20 }}
                  >
                    <Box
                      sx={{
                        width: '20px',
                        height: '10px',
                        background: row.color,
                      }}
                    />
                  </TableCell>
                  <TableCell
                    component="th"
                    scope="row"
                    style={{
                      color: theme.palette.custom.inputLabel,
                      fontSize: 12,
                      padding: '10px 12px',
                    }}
                  >
                    {row.label}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {state.view === 'country' &&
            state.chartList.length > 1 &&
            showListNumber < state.chartList.length && (
              <Box
                sx={{
                  fontSize: 12,
                  color: theme.palette.custom.dialogButton,
                  textAlign: 'center',
                  padding: '24px 0 12px',
                }}
              >
                <Box
                  component="span"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    setShowListNumber(showListNumber + 8)
                  }}
                >
                  Load more
                  <ArrowForwardIosIcon
                    sx={{
                      fontSize: '12px !important',
                      transform: 'rotate(90deg)',
                      marginLeft: '4px',
                      verticalAlign: 'middle',
                      color: theme.palette.custom.dialogButton,
                    }}
                  />
                </Box>
              </Box>
            )}
        </TableContainer>
      </Popover>
    </>
  )
}
export default SortButton
